import React, { useEffect } from "react";

const WA_LINK = "https://wa.me/+5493412275598?text=Hola%20estoy%20interesado%20en%20adquirir%20un%20collar%20smartpet";

function About() {
    useEffect(() => {
        document.title = "Nosotros | SmartPet - Collares inteligentes para mascotas";
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute(
                "content",
                "Conocé la historia de SmartPet, nuestra misión y el equipo detrás de los collares inteligentes con QR y NFC para mascotas."
            );
        }
    }, []);

    return (
        <>


            <main className="min-h-screen bg-[#f4f1f9] font-nunito text-[#2a1f3d]">

                {/* ====== HERO ====== */}
                <section className="bg-[#4a3b7a] text-center py-16 px-4 md:py-20 md:px-8 lg:py-28 lg:px-12 relative overflow-hidden">
                    <p className="text-white/60 text-xs md:text-sm font-bold tracking-[2px] uppercase mb-2">
                        Una historia real
                    </p>
                    <h1 className="text-white text-3xl md:text-4xl lg:text-5xl font-black max-w-3xl mx-auto leading-tight">
                        Todo empezó con un <span className="text-[#f7c3dc]">error</span> en una chapita
                    </h1>
                    <p className="text-white/80 text-base md:text-lg font-semibold max-w-xl mx-auto mt-3 leading-relaxed">
                        Perder a Minerva me enseñó que un número grabado no es suficiente.
                        Un error humano puede separarte de tu mascota para siempre.
                    </p>
                    {/* Curva inferior */}
                    <div className="absolute bottom-0 left-0 right-0 h-12 md:h-16 bg-[#f4f1f9]" style={{ clipPath: 'ellipse(55% 100% at 50% 100%)' }} />
                </section>

                {/* ====== CONTENIDO ====== */}
                <div className="max-w-5xl lg:max-w-6xl mx-auto px-4 md:px-6 lg:px-8">

                    {/* ====== HISTORIA ====== */}
                    <section className="py-12 md:py-16 lg:py-20">
                        <div className="flex flex-col lg:flex-row-reverse items-center gap-8 lg:gap-12">
                            <div className="flex-1">
                                <span className="inline-block bg-[#ede8f9] text-[#6c5c94] text-xs font-extrabold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
                                    El origen
                                </span>
                                <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#4a3b7a] mb-4">
                                    Mayo de 2023 — la pesadilla que lo cambió todo
                                </h2>
                                <p className="text-[#3f3d56] font-semibold text-base md:text-lg leading-relaxed mb-3">
                                    Minerva se perdió en Rosario. Tenía su chapita con mi número de teléfono,
                                    pero quien la encontró lo leyó mal. Falló lo más básico.
                                    Por suerte, el segundo número funcionó y pude recuperarla,
                                    pero esa angustia no se la deseo a nadie.
                                </p>
                                <p className="text-[#3f3d56] font-semibold text-base md:text-lg leading-relaxed mb-3">
                                    En ese momento estaba aprendiendo a programar. Un video sobre chips NFC,
                                    un curso de desarrollo web y esta experiencia se combinaron en una idea clara:
                                    un collar que nunca falle en lo más importante, que alguien pueda contactarte
                                    sin depender de que un número se lea bien.
                                </p>
                                <p className="text-[#3f3d56] font-semibold text-base md:text-lg leading-relaxed">
                                    Así nació SmartPet. No es GPS, no es una promesa de tecnología complicada.
                                    Es un sistema simple: un escaneo, una ubicación, un mail. Sin suscripción,
                                    sin baterías, sin errores humanos. Solo tranquilidad.
                                </p>
                            </div>
                            <div className="flex-1 text-center">
                                <img
                                    src="./assets/img-jm-minerva.jpg"
                                    alt="Juan Manuel Toniolo con Minerva, la perrita que inspiró SmartPet"
                                    className="w-full max-w-sm lg:max-w-md mx-auto rounded-2xl shadow-xl hover:scale-[1.02] transition-transform duration-300"
                                    loading="lazy"
                                    width="400"
                                    height="300"
                                />
                                <p className="text-sm text-[#888] font-bold mt-2">Juan Manuel y Minerva 🐾</p>
                            </div>
                        </div>
                    </section>

                    {/* ====== MISIÓN, VISIÓN, PRODUCTOS ====== */}
                    <section className="bg-white rounded-3xl px-6 py-10 md:p-12 lg:p-16 shadow-md -mx-4 md:mx-0 mb-12 md:mb-16">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                            <div className="bg-[#f8f5fc] rounded-2xl p-6 border-t-4 border-[#cd7fa7] hover:-translate-y-1 hover:shadow-lg transition-all">
                                <div className="text-3xl mb-2">🎯</div>
                                <h3 className="text-xl font-black text-[#6c5c94] mb-2">Nuestra misión</h3>
                                <p className="text-[#3f3d56] font-semibold leading-relaxed">
                                    Que ninguna mascota se pierda para siempre por un error humano.
                                    Queremos que cada collar sea una puerta de regreso a casa,
                                    sin importar quién lo encuentre.
                                </p>
                            </div>
                            <div className="bg-[#f8f5fc] rounded-2xl p-6 border-t-4 border-[#cd7fa7] hover:-translate-y-1 hover:shadow-lg transition-all">
                                <div className="text-3xl mb-2">🔭</div>
                                <h3 className="text-xl font-black text-[#6c5c94] mb-2">Nuestra visión</h3>
                                <p className="text-[#3f3d56] font-semibold leading-relaxed">
                                    Un mundo donde los dueños puedan actualizar la información de su mascota
                                    en cualquier momento, sin reemplazar el collar. Datos siempre correctos,
                                    siempre accesibles, de por vida.
                                </p>
                            </div>
                            <div className="bg-[#f8f5fc] rounded-2xl p-6 border-t-4 border-[#cd7fa7] hover:-translate-y-1 hover:shadow-lg transition-all">
                                <div className="text-3xl mb-2">🛠️</div>
                                <h3 className="text-xl font-black text-[#6c5c94] mb-2">Qué hacemos</h3>
                                <p className="text-[#3f3d56] font-semibold leading-relaxed">
                                    Diseñamos collares de resina epoxi o sublimados, personalizados con el nombre
                                    de tu mascota y un código QR. Al escanearlo, se accede a un perfil digital
                                    con tus datos de contacto y un botón para llamarte al instante.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* ====== EQUIPO ====== */}
                    <section className="py-8 md:py-12">
                        <span className="inline-block bg-[#ede8f9] text-[#6c5c94] text-xs font-extrabold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
                            El equipo
                        </span>
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#4a3b7a] mb-4">
                            Un proyecto hecho con propósito
                        </h2>
                        <div className="bg-white rounded-2xl p-6 md:p-8 lg:p-10 shadow-md border-l-4 border-[#6c5c94] flex flex-col md:flex-row items-start gap-6 md:gap-8">
                            <div className="w-16 h-16 rounded-full bg-[#6c5c94] text-white text-xl font-black flex items-center justify-center flex-shrink-0">
                                JM
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-[#2a1f3d]">Juan Manuel Toniolo</h3>
                                <p className="text-[#cd7fa7] font-bold text-sm mb-3">Fundador & Desarrollador</p>
                                <p className="text-[#3f3d56] font-semibold leading-relaxed mb-4">
                                    Trabajo solo en la programación, pero con el apoyo de amigos y
                                    familia que creen en este proyecto. Avanzo a mi ritmo —
                                    tengo empleo a tiempo completo — pero cada hora libre la
                                    dedico a SmartPet con la misma convicción del primer día.
                                </p>
                                <button
                                    onClick={() => window.open(WA_LINK, "_blank", "noopener,noreferrer")}
                                    className="bg-[#25D366] hover:bg-[#1eb858] text-white font-extrabold text-base px-6 py-3 rounded-full shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
                                    type="button"
                                >
                                    Escribirme por WhatsApp 💬
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* ====== VALORES ====== */}
                    <section className="bg-white rounded-3xl px-6 py-10 md:p-12 lg:p-16 shadow-md -mx-4 md:mx-0 my-12 md:my-16">
                        <span className="inline-block bg-[#ede8f9] text-[#6c5c94] text-xs font-extrabold uppercase tracking-wider px-3 py-1 rounded-full mb-3 mx-auto block text-center">
                            Lo que nos mueve
                        </span>
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#4a3b7a] text-center mb-8">
                            Nuestros valores
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div className="bg-[#f8f5fc] rounded-2xl p-6 hover:-translate-y-1 hover:shadow-md transition-all">
                                <span className="text-2xl block mb-2">❤️</span>
                                <h3 className="text-lg font-black text-[#6c5c94] mb-1">Empatía</h3>
                                <p className="text-[#555] font-semibold text-sm leading-relaxed">
                                    Sabemos lo que se siente perder una mascota. Cada collar nació de una experiencia real.
                                </p>
                            </div>
                            <div className="bg-[#f8f5fc] rounded-2xl p-6 hover:-translate-y-1 hover:shadow-md transition-all">
                                <span className="text-2xl block mb-2">🔍</span>
                                <h3 className="text-lg font-black text-[#6c5c94] mb-1">Honestidad</h3>
                                <p className="text-[#555] font-semibold text-sm leading-relaxed">
                                    Sin promesas exageradas. SmartPet no rastrea en tiempo real; avisa cuando alguien escanea el collar.
                                </p>
                            </div>
                            <div className="bg-[#f8f5fc] rounded-2xl p-6 hover:-translate-y-1 hover:shadow-md transition-all">
                                <span className="text-2xl block mb-2">⚡</span>
                                <h3 className="text-lg font-black text-[#6c5c94] mb-1">Simplicidad</h3>
                                <p className="text-[#555] font-semibold text-sm leading-relaxed">
                                    Sin apps, sin baterías, sin suscripciones. Tecnología que funciona sin que tengas que pensar en ella.
                                </p>
                            </div>
                            <div className="bg-[#f8f5fc] rounded-2xl p-6 hover:-translate-y-1 hover:shadow-md transition-all">
                                <span className="text-2xl block mb-2">🌱</span>
                                <h3 className="text-lg font-black text-[#6c5c94] mb-1">Crecimiento</h3>
                                <p className="text-[#555] font-semibold text-sm leading-relaxed">
                                    Un emprendimiento que avanza despacio pero con propósito, mejorando con cada collar que sale.
                                </p>
                            </div>
                        </div>
                    </section>

                </div>
            </main>

        </>
    );
}

export default About;